import { useEffect, useCallback, useRef } from 'react';

// Default notification sound frequencies (using Web Audio API generated tones)
const SOUND_FREQUENCIES = {
  chime: [523, 659, 784], // C5, E5, G5 - Pleasant chime
  bell: [440, 554, 659],  // A4, C#5, E5 - Bell-like
  alert: [880, 660, 880], // A5, E5, A5 - Urgent
  ping: [1047],           // C6 - Single high ping
};

/**
 * Hook to handle notification sounds (both default and custom MP3)
 */
export function useNotificationSound() {
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);

  // Initialize AudioContext on first user interaction
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play a generated tone sequence (for default sounds)
  const playTone = useCallback((frequencies, duration = 0.15) => {
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        const startTime = ctx.currentTime + (index * duration);
        const endTime = startTime + duration;
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, endTime);
        
        oscillator.start(startTime);
        oscillator.stop(endTime + 0.1);
      });
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }, [initAudio]);

  // Play a custom MP3/audio file
  const playCustomSound = useCallback((url) => {
    try {
      // Stop any currently playing audio
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
      }

      // Create new audio element
      const audio = new Audio(url);
      audio.volume = 0.7;
      audioElementRef.current = audio;
      
      audio.play().catch(e => {
        console.warn('Could not play custom sound:', e);
      });
    } catch (e) {
      console.warn('Error playing custom sound:', e);
    }
  }, []);

  // Play notification sound by type (handles both default and custom)
  const playSound = useCallback((soundType, customUrl = null) => {
    if (soundType === 'none') return;
    
    // If it's a custom sound (starts with 'custom_'), use the URL
    if (soundType?.startsWith('custom_') && customUrl) {
      playCustomSound(customUrl);
      return;
    }
    
    // If a direct URL is provided, play it
    if (customUrl) {
      playCustomSound(customUrl);
      return;
    }
    
    // Otherwise, use default generated tones
    const frequencies = SOUND_FREQUENCIES[soundType] || SOUND_FREQUENCIES.chime;
    playTone(frequencies);
  }, [playTone, playCustomSound]);

  // Listen for service worker messages to play sounds
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        const { sound, soundUrl } = event.data;
        playSound(sound, soundUrl);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [playSound]);

  // Play sound for testing (accepts both type and URL)
  const testSound = useCallback((soundType, customUrl = null) => {
    // Resume audio context (required after user interaction)
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    playSound(soundType, customUrl);
  }, [playSound]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }
    };
  }, []);

  return { playSound, testSound, initAudio, playCustomSound };
}

export default useNotificationSound;
